import React from 'react';
import classNames from 'classnames';
import { throttle } from 'lodash';

import { getCoords } from '../../../browser/utils/dom';
import { EPosition } from '../../filters/components/filters/filters';
import styles from './FiltersSection.css';

interface IFiltersSectionProps {
  setUpdateHeightCb?(cb: () => void): void;
}

interface IFiltersSectionState {
  minHeight: number;
  position: EPosition;
  withoutTransition: boolean;
}

export const FiltersSection: React.FC<IFiltersSectionProps> = ({ children, setUpdateHeightCb }) => {
  const filtersRef = React.useRef<HTMLDivElement>(null);
  const currentScrollTop = React.useRef<number>(0);
  const filtersOffsetTop = React.useRef<number>(0);
  const initialized = React.useRef<boolean>(false);

  const [{ minHeight, position, withoutTransition }, setState] = React.useState<IFiltersSectionState>({
    minHeight: 0,
    position: EPosition.default,
    withoutTransition: false,
  });

  const updateFiltersHeight = React.useCallback(() => {
    if (!filtersRef.current) {
      return;
    }

    const filtersHeight = Math.round(filtersRef.current.getBoundingClientRect().height);

    if (filtersHeight !== minHeight) {
      setState(state => ({ ...state, minHeight: filtersHeight }));
    }
  }, [minHeight]);

  const handleScroll = React.useMemo(
    () =>
      throttle(() => {
        if (!filtersRef.current) {
          return;
        }

        const filtersHeight = Math.round(filtersRef.current.getBoundingClientRect().height);

        // Если изменилась высота фильтров, то не обрабатываем сработавший скролл
        if (filtersHeight !== minHeight) {
          setState(state => ({ ...state, minHeight: filtersHeight }));

          return;
        }

        const newScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

        const areFiltersVisible = newScrollTop <= filtersOffsetTop.current + filtersHeight;
        const scrollingDown = newScrollTop > currentScrollTop.current;
        const scrollingUp = newScrollTop < currentScrollTop.current;
        currentScrollTop.current = newScrollTop;

        const switchPosition = (newPosition: EPosition) => {
          switch (newPosition) {
            case EPosition.FixedHidden:
              setState(state => ({
                ...state,
                withoutTransition: state.position === EPosition.default,
                position: EPosition.FixedHidden,
              }));
              break;

            case EPosition.Fixed:
              setState(state => ({
                ...state,
                position: EPosition.Fixed,
                withoutTransition: false,
              }));
              break;

            default:
              setState(state => ({ ...state, position: EPosition.default }));
          }
        };

        if (scrollingUp) {
          if (newScrollTop <= filtersOffsetTop.current) {
            switchPosition(EPosition.default);
          } else {
            switchPosition(EPosition.Fixed);
          }
        } else if (scrollingDown) {
          if (!areFiltersVisible) {
            switchPosition(EPosition.FixedHidden);
          }
        }
      }, 50),
    [minHeight],
  );

  React.useEffect(() => {
    function initialize() {
      if (!filtersRef.current) {
        return;
      }
      const coords = getCoords(filtersRef.current);

      filtersOffsetTop.current = coords.top;
      currentScrollTop.current = document.body.scrollTop;

      initialized.current = true;
    }

    if (!initialized.current) {
      initialize();
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [handleScroll]);

  React.useEffect(() => {
    if (setUpdateHeightCb) {
      setUpdateHeightCb(updateFiltersHeight);
    }
  }, [setUpdateHeightCb, updateFiltersHeight]);

  return (
    <div
      style={{ minHeight: `${minHeight}px` }}
      className={classNames(
        styles['filters'],
        (position === EPosition.Fixed || position === EPosition.FixedHidden) && styles['fixed'],
        position === EPosition.FixedHidden && styles['hidden'],
        withoutTransition && styles['notransition'],
      )}
    >
      <div ref={filtersRef} className={classNames(styles['filters-container'], styles['main-filters-container'])}>
        {children}
      </div>
    </div>
  );
};
